/* eslint-disable lingui/no-unlocalized-strings -- Environment names, not UI copy. */
import { env } from "@/client/config/env";

/**
 * Derived from the client URL rather than a dedicated variable because VITE_*
 * values are inlined into the bundle at image build time — there is no runtime
 * config for the browser to read. Order matters: demo.theresume.io and
 * staging.theresume.io both contain the production host.
 */
export const resolveEnvironment = () => {
  if (env.DEV) return "local";

  const url = env.VITE_CLIENT_URL;
  if (url.includes("staging.")) return "staging";
  if (url.includes("demo.")) return "demo";
  if (/^https:\/\/(www\.)?(theresume\.io|firstround\.com\.au)\/?$/.test(url)) return "production";

  return "unknown";
};

/**
 * Environments allowed to send events.
 *
 * Applies to every analytics tool (Mixpanel, GA4). Mixpanel's event quota is counted across the
 * whole organisation, not per project, so staging and demo traffic would eat the same monthly
 * allowance as real candidates while telling us nothing — every user there is one of us. They are
 * switched off entirely rather than separated into another project.
 *
 * "local" is off unless VITE_ANALYTICS_LOCAL=true. It used to be on by default, and on 2026-10-21
 * three quarters of the production project's events came from dev servers (local .env files held
 * the production token). Turn it on only with a throwaway Development project token, to verify
 * tracking before it ships.
 *
 * Temporarily add "staging" here when a deployed-environment problem needs
 * reproducing — CSP and header behaviour only exist once NODE_ENV=production.
 */
export const TRACKED_ENVIRONMENTS = new Set(["production"]);

/**
 * Analytics must never be able to take the app down. A blocked request, an
 * ad-blocker stripping the SDK, or storage being unavailable in a private window
 * are all ordinary conditions, not errors worth surfacing to a candidate.
 */
export const safely = (action: string, run: () => void) => {
  try {
    run();
  } catch (error) {
    if (env.DEV) console.warn(`[analytics] ${action} failed`, error);
  }
};

export const isTrackedEnvironment = (environment = resolveEnvironment()) =>
  TRACKED_ENVIRONMENTS.has(environment) ||
  (environment === "local" && env.VITE_ANALYTICS_LOCAL === true);
